/**
 * Lesson result reporter (CL-146, roadmap H2.2).
 *
 * Buffers one row per scored lesson step (the engine calls
 * reportStepResult() from the same seam that updates scoring) and POSTs the
 * batch to /api/lessons/results at lesson finish/stop, with
 * licenseHeaders() + classroomHeaders() so the Worker can attribute the
 * results to the school (and, when the student joined a class, to their
 * anonymous seat).
 *
 * FAIL-SOFT by contract: persistence must NEVER block or break a lesson.
 * - Anonymous no-license sessions are a complete no-op — persistence is a
 *   licensed-school feature (the Worker would 401 anyway).
 * - Failed batches queue to localStorage ('ev:pending-results', capped) and
 *   retry on the next flush or on app init (retryPendingResults() is called
 *   from main.js).
 * - fetch uses keepalive so an end-of-lesson flush survives a tab close.
 */
import { createLogger } from '../utils/logger.js'
import { getLicenseKey, licenseHeaders } from '../utils/license.js'
import { classroomHeaders } from '../utils/classroom.js'

const log = createLogger('result-reporter')

export const PENDING_RESULTS_KEY = 'ev:pending-results'
/** Max result rows kept in the offline retry queue (oldest dropped first). */
export const PENDING_RESULTS_CAP = 200
/** Mirrors the Worker's LESSON_RESULTS_MAX_BATCH — rows per POST. */
export const RESULTS_MAX_BATCH = 50

/** Outcomes the Worker accepts (migration 0010 CHECK constraint). */
const OUTCOMES = ['correct', 'partial', 'incorrect', 'review', 'skipped']
const MAX_TAGS = 10

const API_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || ''

/** In-memory buffer of Worker-shaped rows for the lesson in progress. */
const _buffer = []

/** Persistence is a licensed-school feature — no license, no reporting. */
export function isReportingEnabled() {
  return Boolean(getLicenseKey())
}

function safeStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    // localStorage access can throw in sandboxed contexts
    return null
  }
}

/** Read the queued offline batches: [{ lesson_id, results: [...] }, ...]. */
function readPending() {
  const storage = safeStorage()
  if (!storage) return []
  try {
    const raw = storage.getItem(PENDING_RESULTS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Persist the queue, dropping OLDEST batches past the row cap. Fail-soft. */
function writePending(batches) {
  const storage = safeStorage()
  if (!storage) return
  try {
    const capped = [...batches]
    let rows = capped.reduce((sum, b) => sum + (b.results?.length || 0), 0)
    while (rows > PENDING_RESULTS_CAP && capped.length > 0) {
      rows -= capped[0].results?.length || 0
      capped.shift()
    }
    if (capped.length === 0) storage.removeItem(PENDING_RESULTS_KEY)
    else storage.setItem(PENDING_RESULTS_KEY, JSON.stringify(capped))
  } catch (err) {
    log.warn('could not persist pending results', err)
  }
}

/**
 * Normalize the engine's step result (submitAnswer() shape — stepId,
 * questionType, verdict, scoreEarned, maxPoints, plus conceptTags injected
 * by the engine from lesson metadata) into the Worker row shape. Returns
 * null for anything unreportable.
 */
function toRow(stepResult) {
  if (!stepResult || typeof stepResult !== 'object') return null
  const stepId = stepResult.stepId ?? stepResult.step_id
  const outcome = stepResult.verdict ?? stepResult.outcome
  if (typeof stepId !== 'string' || !stepId.trim()) return null
  if (!OUTCOMES.includes(outcome)) return null
  const row = {
    step_id: stepId.trim().slice(0, 100),
    question_type:
      typeof (stepResult.questionType ?? stepResult.question_type) === 'string'
        ? (stepResult.questionType ?? stepResult.question_type).slice(0, 100)
        : null,
    outcome,
  }
  const score = Number(stepResult.scoreEarned ?? stepResult.score)
  const maxScore = Number(stepResult.maxPoints ?? stepResult.max_score)
  if (Number.isFinite(score)) row.score = score
  if (Number.isFinite(maxScore)) row.max_score = maxScore
  const tags = stepResult.conceptTags ?? stepResult.concept_tags
  if (Array.isArray(tags)) {
    const cleaned = tags
      .filter((t) => typeof t === 'string' && t.trim())
      .map((t) => t.trim().slice(0, 100))
      .slice(0, MAX_TAGS)
    if (cleaned.length) row.concept_tags = cleaned
  }
  if (Number.isInteger(stepResult.attempt) && stepResult.attempt >= 1) {
    row.attempt = stepResult.attempt
  }
  // CL-151: which transition edge the outcome selected ('pass'|'fail').
  // The Worker currently ignores unknown row fields (whitelist copy in
  // validateLessonResultsPayload), so this is forward-compatible telemetry —
  // persisting it is a future Worker migration.
  const branch = stepResult.branchTaken ?? stepResult.branch_taken
  if (branch === 'pass' || branch === 'fail') {
    row.branch_taken = branch
  }
  return row
}

/**
 * Buffer one scored step. Called by the lesson engine at the same seam that
 * updates scoring. No-op for anonymous (unlicensed) sessions and for
 * anything that doesn't normalize to a valid row. Never throws.
 */
export function reportStepResult(stepResult) {
  try {
    if (!isReportingEnabled()) return
    const row = toRow(stepResult)
    if (!row) return
    _buffer.push(row)
    // Runaway guard — the buffer should never outgrow the offline cap.
    if (_buffer.length > PENDING_RESULTS_CAP) _buffer.shift()
  } catch (err) {
    log.warn('reportStepResult failed (ignored)', err)
  }
}

/** POST one batch. Resolves true on 2xx, false on anything else. */
async function postBatch(batch) {
  try {
    const resp = await fetch(`${API_URL}/api/lessons/results`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...licenseHeaders(),
        ...classroomHeaders(),
      },
      body: JSON.stringify(batch),
    })
    return resp.ok
  } catch {
    return false
  }
}

/**
 * Send every queued batch (oldest first); whatever fails stays queued for
 * the next flush/init. Called from main.js on startup and by flushResults().
 * Never throws.
 */
export async function retryPendingResults() {
  try {
    if (!isReportingEnabled()) return false
    const pending = readPending()
    if (pending.length === 0) return true
    const failed = []
    for (const batch of pending) {
      if (failed.length > 0 || !(await postBatch(batch))) {
        // First failure — keep order, don't hammer a down endpoint.
        failed.push(batch)
      }
    }
    writePending(failed)
    return failed.length === 0
  } catch (err) {
    log.warn('retryPendingResults failed (ignored)', err)
    return false
  }
}

/**
 * Flush the buffered rows for a finished/stopped lesson: earlier queued
 * batches go first (oldest first), then the current lesson's batches. The
 * buffer is always drained — even for unlicensed sessions, so a mid-session
 * license removal can't leak rows into the next lesson. Fail-soft: failed
 * batches land in the localStorage queue for the next flush/init. Never
 * throws; resolves true only when everything was delivered.
 */
export async function flushResults(lessonId) {
  try {
    const rows = _buffer.splice(0, _buffer.length)
    if (!isReportingEnabled()) return false

    const pendingOk = await retryPendingResults()

    if (rows.length === 0 || typeof lessonId !== 'string' || !lessonId.trim()) {
      return pendingOk
    }
    const id = lessonId.trim().slice(0, 100)
    const batches = []
    for (let i = 0; i < rows.length; i += RESULTS_MAX_BATCH) {
      batches.push({ lesson_id: id, results: rows.slice(i, i + RESULTS_MAX_BATCH) })
    }
    const failed = []
    for (const batch of batches) {
      // Once anything failed (incl. the pending retry), stop hammering a
      // down endpoint and queue the rest in order.
      if (!pendingOk || failed.length > 0 || !(await postBatch(batch))) {
        failed.push(batch)
      }
    }
    if (failed.length > 0) writePending([...readPending(), ...failed])
    return pendingOk && failed.length === 0
  } catch (err) {
    log.warn('flushResults failed (ignored)', err)
    return false
  }
}
